/**
 * Booking Service
 * Handles API interactions for booking submissions.
 * Provides methods for submitting booking requests to the backend.
 *
 * @module bookingService
 */

import api from '../utils/apiv2';

/**
 * Booking Service class
 * Manages booking submissions through backend API
 */
class BookingService {
  /**
   * Submit a booking request
   * @param {Object} bookingData - Booking payload
   * @param {string} bookingData.offerId - ID of the offer being booked
   * @param {string} bookingData.userId - ID of the user making the booking
   * @param {Object} bookingData.offer - Complete offer details
   * @param {string} bookingData.offer.model - Vehicle model name
   * @param {string} bookingData.offer.group - Vehicle group/category code
   * @param {string} bookingData.offer.pickupLocation - Pickup location code
   * @param {string} bookingData.offer.dropoffLocation - Drop-off location code
   * @param {string} bookingData.offer.pickupDate - Pickup date and time
   * @param {string} bookingData.offer.dropoffDate - Drop-off date and time
   * @param {number} bookingData.offer.rentalDays - Number of rental days
   * @param {Object} bookingData.offer.specifications - Vehicle specifications
   * @param {Object} bookingData.offer.terms - Rental terms and conditions
   * @param {Object} bookingData.offer.payment - Payment details
   * @param {Object} bookingData.offer.supplier - Supplier information
   * @param {Object} bookingData.customer - Customer details
   * @param {string} bookingData.customer.firstName - Customer first name
   * @param {string} bookingData.customer.lastName - Customer last name
   * @param {string} bookingData.customer.email - Customer email
   * @param {string} bookingData.customer.phone - Customer phone
   * @param {Object} bookingData.customer.address - Customer address
   * @param {string} bookingData.customer.address.street - Street address (street name and number)
   * @param {string} bookingData.customer.address.city - City
   * @param {string} bookingData.customer.address.country - Country
   * @param {string} bookingData.customer.dob - Date of birth
   * @param {string} [bookingData.flightNumber] - Flight number (airport bookings)
   * @param {string} [bookingData.landingTime] - Landing time (airport bookings)
   * @param {string} bookingData.notes - Additional notes
   * @returns {Promise<Object>} Booking response with reservationId and status
   */
  async submitBooking(bookingData) {
    try {
      if (!bookingData || !bookingData.offerId || !bookingData.userId || !bookingData.customer) {
        throw new Error('Invalid booking data provided');
      }

      const response = await api.post('/user/booking-requests', bookingData);
      return response.data;
    } catch (error) {
      console.error('BookingService: Error submitting booking', error);
      throw error;
    }
  }
}

export default new BookingService();